import React, { useState, useRef, useEffect } from 'react';
import { PiCaretDown, PiCaretRight } from 'react-icons/pi';
import { NavLink, useInRouterContext } from 'react-router-dom';

const MobileMenuItem = ({
    name, // Name of the menu item to display. 
    link, // Link to navigate to when the menu item is clicked (if no submenus).
    menus, // Array of submenu items (null if no submenus).
    parentRef, // Reference to the parent menu item.
    setSelected, // Function to set the selected menu item.
    onEnter, // Function to handle mouse enter event.
    onLeave, // Function to handle mouse leave event.
    closeParentDropDown, // Function to close the parent dropdown menu (if any).
    closeMobileMenu, // Function to close the mobile menu.
    depth = 0
}) => {
    // State to track whether the submenu is dropped down or not.
    const [droppedDown, setDroppedDown] = useState(false);
    // Ref for the dropdown submenu, used to manage scroll behavior.
    const dropDownRef = useRef(null);
    // Check if the component is rendered within a router context (for navigation purposes).
    const isInRouterContext = useInRouterContext();

    // Auto-scroll into view when submenu opens
    useEffect(() => {
        if (droppedDown && dropDownRef.current) {
            setTimeout(() => {
                dropDownRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }, 100); // Delay to ensure the dropdown is rendered before scrolling
        }
    }, [droppedDown]);

    // Get padding based on depth
    const getPaddingClass = () => {
        if (depth === 0) return '';
        if (depth === 1) return 'pl-6';
        if (depth === 2) return 'pl-10';
        return 'pl-14';
    };

    // Get background color based on depth
    const getBgClass = () => {
        if (depth === 0) return 'bg-white';
        if (depth === 1) return 'bg-gray-50';
        if (depth === 2) return 'bg-gray-100';
        return 'bg-gray-100';
    };

    // Toggle dropdown (expand/collapse submenu)
    const toggleDropdown = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        if (menus && menus.length > 0) {
            setDroppedDown(!droppedDown);
        }
    };

    // Handle click
    const handleClick = (e) => {
        e.stopPropagation();
        
        if (menus && menus.length > 0) {
            toggleDropdown(e);
        } else {
            // Navigate and close menu
            if (setSelected) setSelected(name);
            if (closeParentDropDown) closeParentDropDown();
            if (closeMobileMenu) closeMobileMenu();
        }
    };

    return (
        <div
            className={`w-full border-b border-gray-100 mobile-menu-item ${getBgClass()}`}
        >
            <div 
                className={`w-full ${getPaddingClass()}`}
                onClick={handleClick}
            >
                {menus && menus.length > 0 ? (
                    // Item with submenu
                    <div className="flex flex-row w-full justify-between items-center text-sm tracking-wider px-4 py-3 capitalize font-nunitoSansSemiBold">
                        <span>{name}</span>
                        <span className="flex items-center ml-2">
                            {parentRef ? (
                                <PiCaretRight 
                                    size={16} 
                                    className={`transition-transform duration-300 ${
                                        droppedDown ? 'rotate-90' : ''
                                    }`}
                                />
                            ) : (
                                <PiCaretDown 
                                    size={16} 
                                    className={`transition-transform duration-300 ${
                                        droppedDown ? 'rotate-180' : ''
                                    }`}
                                />
                            )}
                        </span>
                    </div>
                ) : (
                    // Item without submenu (link)
                    /^https?:\/\//.test(link || '') ? (
                        <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full block text-sm tracking-wider px-4 py-3 capitalize font-nunitoSansSemiBold text-[rgb(68,71,70)]"
                            onClick={() => {
                                if (closeMobileMenu) closeMobileMenu();
                            }}
                        >
                            {name}
                        </a>
                    ) : isInRouterContext ? (
                        <NavLink 
                            to={link || '/'} 
                            className={({ isActive }) => 
                                `w-full block text-sm tracking-wider px-4 py-3 capitalize font-nunitoSansSemiBold ${
                                    isActive ? 'text-[rgb(0,175,240)]' : 'text-[rgb(68,71,70)]'
                                }`
                            }
                            onClick={() => {
                                if (closeMobileMenu) closeMobileMenu();
                            }}
                        >
                            {name}
                        </NavLink>
                    ) : (
                        <div className="w-full text-sm tracking-wider px-4 py-3 capitalize font-nunitoSansSemiBold">
                            {name}
                        </div>
                    )
                )}
            </div>

            {/* Submenu */}
            {menus && menus.length > 0 && (
                <div 
                    ref={dropDownRef}
                    className={`relative w-full overflow-visible transition-all duration-300 ${
                        droppedDown ? 'block' : 'hidden'
                    }`}
                    style={{
                        borderLeft: depth < 3 ? '2px solid #e5e7eb' : 'none',
                        marginLeft: depth < 3 ? '4px' : '0'
                    }}
                >
                    <div className="flex flex-col w-full">
                        {menus.map((menu, i) => (
                            <MobileMenuItem
                                key={i}
                                name={menu.name || 'null'}
                                link={menu.link || '/'}
                                menus={menu.menus?.length > 0 ? menu.menus : null}
                                parentRef={true}
                                setSelected={setSelected}
                                onEnter={onEnter}
                                onLeave={onLeave}
                                closeParentDropDown={closeParentDropDown}
                                closeMobileMenu={closeMobileMenu}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileMenuItem;